<template>
    <div
        :class="[
            type === 'textarea' ? 'iu-textarea' : 'iu-input',
            inputSize ? 'iu-input--' + inputSize : '',
            {
                'is-disabled': inputDisabled,
                'is-exceed': inputExceed,
                'iu-input-group': $slots.prepend || $slots.append,
                'iu-input-group--append': $slots.append,
                'iu-input-group--prepend': $slots.prepend,
                'iu-input--prefix': $slots.prefix || prefixIcon,
                'iu-input--suffix': $slots.suffix || suffixIcon || clearable || showPassword,
            },
        ]"
        @mouseenter="hovering = true"
        @mouseleave="hovering = false"
    >
        <template v-if="type !== 'textarea'">
            <!-- Leading element -->
            <div class="iu-input-group__prepend" v-if="$slots.prepend">
                <slot name="prepend"></slot>
            </div>
            <input
                :tabindex="tabindex"
                v-if="type !== 'textarea'"
                class="iu-input__inner"
                v-bind="$attrs"
                :type="showPassword ? (passwordVisible ? 'text' : 'password') : type"
                :disabled="inputDisabled"
                :readonly="readonly"
                :autocomplete="autocomplete"
                ref="input"
                @compositionstart="handleCompositionStart"
                @compositionupdate="handleCompositionUpdate"
                @compositionend="handleCompositionEnd"
                @input="handleInput"
                @focus="handleFocus"
                @blur="handleBlur"
                @change="handleChange"
                :aria-label="label"
            />
            <!-- Front content -->
            <span class="iu-input__prefix" v-if="$slots.prefix || prefixIcon">
                <slot name="prefix"></slot>
                <i class="iu-input__icon" v-if="prefixIcon" :class="prefixIcon"></i>
            </span>
            <!-- Post content -->
            <span class="iu-input__suffix" v-if="getSuffixVisible()">
                <span class="iu-input__suffix-inner">
                    <template v-if="!showClear || !showPwdVisible || !isWordLimitVisible">
                        <slot name="suffix"></slot>
                        <i class="iu-input__icon" v-if="suffixIcon" :class="suffixIcon"></i>
                    </template>
                    <i
                        v-if="showClear"
                        class="iu-input__icon iu-icon-circle-close iu-input__clear"
                        @mousedown.prevent
                        @click="clear"
                    ></i>
                    <i
                        v-if="showPwdVisible"
                        class="iu-input__icon iu-icon-view iu-input__clear"
                        @click="handlePasswordVisible"
                    ></i>
                    <span v-if="isWordLimitVisible" class="iu-input__count">
                        <span class="iu-input__count-inner">{{ textLength }}/{{ upperLimit }}</span>
                    </span>
                </span>
                <i class="iu-input__icon" v-if="validateState" :class="['iu-input__validateIcon', validateIcon]"></i>
            </span>
            <!-- Post element -->
            <div class="iu-input-group__append" v-if="$slots.append">
                <slot name="append"></slot>
            </div>
        </template>
        <textarea
            v-else
            :tabindex="tabindex"
            class="iu-textarea__inner"
            @compositionstart="handleCompositionStart"
            @compositionupdate="handleCompositionUpdate"
            @compositionend="handleCompositionEnd"
            @input="handleInput"
            ref="textarea"
            v-bind="$attrs"
            :disabled="inputDisabled"
            :readonly="readonly"
            :autocomplete="autocomplete"
            :style="textareaStyle"
            @focus="handleFocus"
            @blur="handleBlur"
            @change="handleChange"
            :aria-label="label"
        ></textarea>
        <span v-if="isWordLimitVisible && type === 'textarea'" class="iu-input__count">
            {{ textLength }}/{{ upperLimit }}
        </span>
    </div>
</template>
<script>
import emitter from '../../../utils/emitter';
import calcTextareaHeight from '../../../utils/input';
import merge from '../../../utils/merge';
import { isKorean } from '../../../utils/shared';
export default {
    name: 'IuInput',
    mixins: [emitter],
    inheritAttrs: false,
    inject: {
        elForm: {
            default: '',
        },
        elFormItem: {
            default: '',
        },
    },
    data() {
        return {
            textareaCalcStyle: {},
            hovering: false,
            focused: false,
            isComposing: false,
            passwordVisible: false,
        };
    },
    props: {
        value: [String, Number],
        size: String,
        resize: String,
        form: String,
        disabled: Boolean,
        readonly: Boolean,
        type: {
            type: String,
            default: 'text',
        },
        autosize: {
            type: [Boolean, Object],
            default: false,
        },
        autocomplete: {
            type: String,
            default: 'off',
        },
        validateEvent: {
            type: Boolean,
            default: true,
        },
        suffixIcon: String,
        prefixIcon: String,
        label: String,
        clearable: {
            type: Boolean,
            default: false,
        },
        showPassword: {
            type: Boolean,
            default: false,
        },
        showWordLimit: {
            type: Boolean,
            default: false,
        },
        tabindex: String,
    },
    computed: {
        _elFormItemSize() {
            return (this.elFormItem || {}).elFormItemSize;
        },
        validateState() {
            return this.elFormItem ? this.elFormItem.validateState : '';
        },
        needStatusIcon() {
            return this.elForm ? this.elForm.statusIcon : false;
        },
        validateIcon() {
            return {
                validating: 'iu-icon-loading',
                success: 'iu-icon-circle-check',
                error: 'iu-icon-circle-close',
            }[this.validateState];
        },
        textareaStyle() {
            return merge({}, this.textareaCalcStyle, { resize: this.resize });
        },
        inputSize() {
            return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
        },
        inputDisabled() {
            return this.disabled || (this.elForm || {}).disabled;
        },
        nativeInputValue() {
            return this.value === null || this.value === undefined ? '' : String(this.value);
        },
        showClear() {
            return (
                this.clearable &&
                !this.inputDisabled &&
                !this.readonly &&
                this.nativeInputValue &&
                (this.focused || this.hovering)
            );
        },
        showPwdVisible() {
            return (
                this.showPassword && !this.inputDisabled && !this.readonly && (!!this.nativeInputValue || this.focused)
            );
        },
        isWordLimitVisible() {
            return (
                this.showWordLimit &&
                this.$attrs.maxlength &&
                (this.type === 'text' || this.type === 'textarea') &&
                !this.inputDisabled &&
                !this.readonly &&
                !this.showPassword
            );
        },
        upperLimit() {
            return this.$attrs.maxlength;
        },
        textLength() {
            if (typeof this.value === 'number') {
                return String(this.value).length;
            }
            return (this.value || '').length;
        },
        inputExceed() {
            // show exceed style if length of initial value greater then maxlength
            return this.isWordLimitVisible && this.textLength > this.upperLimit;
        },
    },
    watch: {
        value(val) {
            this.$nextTick(this.resizeTextarea);
            if (this.validateEvent) {
                this.dispatch('ElFormItem', 'el.form.change', [val]);
            }
        },
        // native input value is set explicitly
        // do not use v-model / :value in template
        // see: https://github.com/ElemeFE/element/issues/14521
        nativeInputValue() {
            this.setNativeInputValue();
        },
        // when change between <input> and <textarea>,
        // update DOM dependent value and styles
        // https://github.com/ElemeFE/element/issues/14857
        type() {
            this.$nextTick(() => {
                this.setNativeInputValue();
                this.resizeTextarea();
                this.updateIconOffset();
            });
        },
    },
    methods: {
        focus() {
            this.getInput().focus();
        },
        blur() {
            this.getInput().blur();
        },
        handleBlur(event) {
            this.focused = false;
            this.$emit('blur', event);
            if (this.validateEvent) {
                this.dispatch('ElFormItem', 'el.form.blur', [this.value]);
            }
        },
        select() {
            this.getInput().select();
        },
        resizeTextarea() {
            if (this.$isServer) return;
            const { autosize, type } = this;
            if (type !== 'textarea') return;
            if (!autosize) {
                this.textareaCalcStyle = {
                    minHeight: calcTextareaHeight(this.$refs.textarea).minHeight,
                };
                return;
            }
            const minRows = autosize.minRows;
            const maxRows = autosize.maxRows;
            this.textareaCalcStyle = calcTextareaHeight(this.$refs.textarea, minRows, maxRows);
        },
        setNativeInputValue() {
            const input = this.getInput();
            if (!input) return;
            if (input.value === this.nativeInputValue) return;
            input.value = this.nativeInputValue;
        },
        handleFocus(event) {
            this.focused = true;
            this.$emit('focus', event);
        },
        handleCompositionStart() {
            this.isComposing = true;
        },
        handleCompositionUpdate(event) {
            const text = event.target.value;
            const lastCharacter = text[text.length - 1] || '';
            this.isComposing = !isKorean(lastCharacter);
        },
        handleCompositionEnd(event) {
            if (this.isComposing) {
                this.isComposing = false;
                this.handleInput(event);
            }
        },
        handleInput(event) {
            // should not emit input during composition
            // see: https://github.com/ElemeFE/element/issues/10516
            if (this.isComposing) return;
            // hack for https://github.com/ElemeFE/element/issues/8548
            // should remove the following line when we don't support IE
            if (event.target.value === this.nativeInputValue) return;
            this.$emit('input', event.target.value);
            // ensure native input value is controlled
            // see: https://github.com/ElemeFE/element/issues/12850
            this.$nextTick(this.setNativeInputValue);
        },
        handleChange(event) {
            this.$emit('change', event.target.value);
        },
        calcIconOffset(place) {
            let elList = [].slice.call(this.$el.querySelectorAll(`.iu-input__${place}`) || []);
            if (!elList.length) return;
            let el = null;
            for (let i = 0; i < elList.length; i++) {
                if (elList[i].parentNode === this.$el) {
                    el = elList[i];
                    break;
                }
            }
            if (!el) return;
            const pendantMap = {
                suffix: 'append',
                prefix: 'prepend',
            };
            const pendant = pendantMap[place];
            if (this.$slots[pendant]) {
                el.style.transform = `translateX(${place === 'suffix' ? '-' : ''}${
                    this.$el.querySelector(`.iu-input-group__${pendant}`).offsetWidth
                }px)`;
            } else {
                el.removeAttribute('style');
            }
        },
        updateIconOffset() {
            this.calcIconOffset('prefix');
            this.calcIconOffset('suffix');
        },
        clear() {
            this.$emit('input', '');
            this.$emit('change', '');
            this.$emit('clear');
        },
        handlePasswordVisible() {
            this.passwordVisible = !this.passwordVisible;
            this.focus();
        },
        getInput() {
            return this.$refs.input || this.$refs.textarea;
        },
        getSuffixVisible() {
            return (
                this.$slots.suffix ||
                this.suffixIcon ||
                this.showClear ||
                this.showPassword ||
                this.isWordLimitVisible ||
                (this.validateState && this.needStatusIcon)
            );
        },
    },
    created() {
        this.$on('inputSelect', this.select);
    },
    mounted() {
        this.setNativeInputValue();
        this.resizeTextarea();
        this.updateIconOffset();
    },
    updated() {
        this.$nextTick(this.updateIconOffset);
    },
};
</script>

<style scope>
.iu-textarea {
    position: relative;
    display: inline-block;
    width: 100%;
    vertical-align: bottom;
    font-size: 14px;
}
.iu-textarea__inner {
    display: block;
    resize: vertical;
    padding: 5px 15px;
    line-height: 1.5;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    width: 100%;
    font-size: inherit;
    color: #606266;
    background-color: #fff;
    background-image: none;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    -webkit-transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.iu-textarea__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner::placeholder {
    color: #c0c4cc;
}
.iu-textarea__inner:hover {
    border-color: #c0c4cc;
}
.iu-textarea__inner:focus {
    outline: 0;
    border-color: #409eff;
}
.iu-textarea .iu-input__count {
    color: #909399;
    background: #fff;
    position: absolute;
    font-size: 12px;
    bottom: 5px;
    right: 10px;
}
.iu-textarea.is-disabled .iu-textarea__inner {
    background-color: #f5f7fa;
    border-color: #e4e7ed;
    color: #c0c4cc;
    cursor: not-allowed;
}
.iu-textarea.is-disabled .iu-textarea__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-disabled .iu-textarea__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-disabled .iu-textarea__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-disabled .iu-textarea__inner::placeholder {
    color: #c0c4cc;
}
.iu-textarea.is-exceed .iu-textarea__inner {
    border-color: #f56c6c;
}
.iu-textarea.is-exceed .iu-input__count {
    color: #f56c6c;
}
.iu-input {
    position: relative;
    font-size: 14px;
    display: inline-block;
    width: 100%;
}
.iu-input::-webkit-scrollbar {
    z-index: 11;
    width: 6px;
}
.iu-input::-webkit-scrollbar:horizontal {
    height: 6px;
}
.iu-input::-webkit-scrollbar-thumb {
    border-radius: 5px;
    width: 6px;
    background: #b4bccc;
}
.iu-input::-webkit-scrollbar-corner {
    background: #fff;
}
.iu-input::-webkit-scrollbar-track {
    background: #fff;
}
.iu-input::-webkit-scrollbar-track-piece {
    background: #fff;
    width: 6px;
}
.iu-input .iu-input__clear {
    color: #c0c4cc;
    font-size: 14px;
    cursor: pointer;
    -webkit-transition: color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.iu-input .iu-input__clear:hover {
    color: #909399;
}
.iu-input .iu-input__count {
    height: 100%;
    display: -webkit-inline-box;
    display: -ms-inline-flexbox;
    display: inline-flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    color: #909399;
    font-size: 12px;
}
.iu-input .iu-input__count .iu-input__count-inner {
    background: #fff;
    line-height: initial;
    display: inline-block;
    padding: 0 5px;
}
.iu-input__inner {
    -webkit-appearance: none;
    background-color: #fff;
    background-image: none;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    color: #606266;
    display: inline-block;
    font-size: inherit;
    height: 40px;
    line-height: 40px;
    outline: 0;
    padding: 0 15px;
    -webkit-transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    width: 100%;
}
.iu-input__prefix,
.iu-input__suffix {
    position: absolute;
    top: 0;
    -webkit-transition: all 0.3s;
    text-align: center;
    height: 100%;
    color: #c0c4cc;
}
.iu-input__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-input__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input__inner::placeholder {
    color: #c0c4cc;
}
.iu-input__inner:hover {
    border-color: #c0c4cc;
}
.iu-input.is-active .iu-input__inner,
.iu-input__inner:focus {
    border-color: #409eff;
    outline: 0;
}
.iu-input__suffix {
    right: 5px;
    transition: all 0.3s;
    pointer-events: none;
}
.iu-input__suffix-inner {
    pointer-events: all;
}
.iu-input__prefix {
    left: 5px;
    transition: all 0.3s;
}
.iu-input__icon {
    height: 100%;
    width: 25px;
    text-align: center;
    -webkit-transition: all 0.3s;
    transition: all 0.3s;
    line-height: 40px;
}
.iu-input__icon:after {
    content: '';
    height: 100%;
    width: 0;
    display: inline-block;
    vertical-align: middle;
}
.iu-input__validateIcon {
    pointer-events: none;
}
.iu-input.is-disabled .iu-input__inner {
    background-color: #f5f7fa;
    border-color: #e4e7ed;
    color: #c0c4cc;
    cursor: not-allowed;
}
.iu-input.is-disabled .iu-input__inner::-webkit-input-placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__inner:-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__inner::-ms-input-placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__inner::placeholder {
    color: #c0c4cc;
}
.iu-input.is-disabled .iu-input__icon {
    cursor: not-allowed;
}
.iu-input.is-exceed .iu-input__inner {
    border-color: #f56c6c;
}
.iu-input.is-exceed .iu-input__suffix .iu-input__count {
    color: #f56c6c;
}
.iu-input--suffix .iu-input__inner {
    padding-right: 30px;
}
.iu-input--prefix .iu-input__inner {
    padding-left: 30px;
}
.iu-input--medium {
    font-size: 14px;
}
.iu-input--medium .iu-input__inner {
    height: 36px;
    line-height: 36px;
}
.iu-input--medium .iu-input__icon {
    line-height: 36px;
}
.iu-input--small {
    font-size: 13px;
}
.iu-input--small .iu-input__inner {
    height: 32px;
    line-height: 32px;
}
.iu-input--small .iu-input__icon {
    line-height: 32px;
}
.iu-input--mini {
    font-size: 12px;
}
.iu-input--mini .iu-input__inner {
    height: 28px;
    line-height: 28px;
}
.iu-input--mini .iu-input__icon {
    line-height: 28px;
}
.iu-input-group {
    line-height: normal;
    display: inline-table;
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}
.iu-input-group > .iu-input__inner {
    vertical-align: middle;
    display: table-cell;
}
.iu-input-group__append,
.iu-input-group__prepend {
    background-color: #f5f7fa;
    color: #909399;
    vertical-align: middle;
    display: table-cell;
    position: relative;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 0 20px;
    width: 1px;
    white-space: nowrap;
}
.iu-input-group--prepend .iu-input__inner,
.iu-input-group__append {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}
.iu-input-group--append .iu-input__inner,
.iu-input-group__prepend {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}
.iu-input-group__append:focus,
.iu-input-group__prepend:focus {
    outline: 0;
}
.iu-input-group__append .iu-button,
.iu-input-group__append .iu-select,
.iu-input-group__prepend .iu-button,
.iu-input-group__prepend .iu-select {
    display: inline-block;
    margin: -10px -20px;
}
.iu-input-group__append button.iu-button,
.iu-input-group__append div.iu-select .iu-input__inner,
.iu-input-group__append div.iu-select:hover .iu-input__inner,
.iu-input-group__prepend button.iu-button,
.iu-input-group__prepend div.iu-select .iu-input__inner,
.iu-input-group__prepend div.iu-select:hover .iu-input__inner {
    border-color: transparent;
    background-color: transparent;
    color: inherit;
    border-top: 0;
    border-bottom: 0;
}
.iu-input-group__append .iu-button,
.iu-input-group__append .iu-input,
.iu-input-group__prepend .iu-button,
.iu-input-group__prepend .iu-input {
    font-size: inherit;
}
.iu-input-group__prepend {
    border-right: 0;
}
.iu-input-group__append {
    border-left: 0;
}
.iu-input-group--append .iu-select .iu-input.is-focus .iu-input__inner,
.iu-input-group--prepend .iu-select .iu-input.is-focus .iu-input__inner {
    border-color: transparent;
}
.iu-input__inner::-ms-clear {
    display: none;
    width: 0;
    height: 0;
}
</style>
